/**
 * Contadores del inbox contados en el SERVIDOR (fn_wa_inbox_counts), no derivados
 * del array de ≤100 conversaciones que el frontend tiene cargado.
 *
 * El problema que resuelve: con >100 conversaciones el sidebar mostraba "100" fijo,
 * y un canal activo podía marcar 0 porque ninguna de sus conversaciones entraba en
 * las 100 más recientes globales. Este hook trae los números reales de un RPC.
 *
 * Es best-effort: si el RPC falla, devuelve null y el consumidor cae al cálculo
 * viejo (mejor un número aproximado que un inbox roto). Se refresca por señal
 * (bump) para engancharlo al realtime sin acoplar este hook a la suscripción.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';

export interface WaInboxCounts {
  total: number;
  unattended: number;
  archived: number;
  byStatus: Record<string, number>;
  byChannel: Record<string, number>;
  byTag: Record<string, number>;
}

interface Params {
  tenantId: string | null | undefined;
  /** Vista de asesor: contar solo lo asignado a él o sin asignar. */
  agentId?: string | null;
  /** Señal para recontar (ej. un contador del realtime). */
  bump?: number;
}

const UNATTENDED_MS = 30 * 60 * 1000;

export function useWaInboxCounts({ tenantId, agentId, bump }: Params): WaInboxCounts | null {
  const [counts, setCounts] = useState<WaInboxCounts | null>(null);

  const refresh = useCallback(async () => {
    if (!tenantId) { setCounts(null); return; }
    const { data, error } = await supabase.rpc('fn_wa_inbox_counts', {
      p_tenant_id: tenantId,
      p_phone_line_id: null,
      p_agent_id: agentId ?? null,
      p_unattended_ms: UNATTENDED_MS,
    });
    if (error) {
      devLog.error('fn_wa_inbox_counts error:', error);
      return; // conserva el último valor bueno; el consumidor tiene fallback
    }
    const r = (data ?? {}) as Record<string, unknown>;
    setCounts({
      total: Number(r.total) || 0,
      unattended: Number(r.unattended) || 0,
      archived: Number(r.archived) || 0,
      byStatus: (r.by_status as Record<string, number>) ?? {},
      byChannel: (r.by_channel as Record<string, number>) ?? {},
      byTag: (r.by_tag as Record<string, number>) ?? {},
    });
  }, [tenantId, agentId]);

  useEffect(() => { void refresh(); }, [refresh, bump]);

  return counts;
}
